import type * as React from 'react'

import { cn } from '../../lib/utils'

const COLUMNS = 5
const ROWS = 3

/**
 * The empty state's mark (DESIGN.md "Empty states and charts"): a 5 by 3
 * grid of ruled square cells, the page ruled and waiting for its first
 * entry, with the framed nuqta in the reading-start cell (the left in
 * English, the right in Arabic). Decoration: always `aria-hidden`.
 */
function KuficGridMark({
  tone = 'default',
  className,
  ...props
}: Omit<React.ComponentProps<'svg'>, 'children'> & { tone?: 'default' | 'committed' }) {
  const committed = tone === 'committed'
  const cells: React.ReactNode[] = []
  for (let row = 0; row < ROWS; row++) {
    for (let column = 0; column < COLUMNS; column++) {
      const first = row === 0 && column === 0
      cells.push(
        <rect
          key={`${row}-${column}`}
          data-first={first || undefined}
          x={column * 4 + 0.5}
          y={row * 4 + 0.5}
          width={3}
          height={3}
          vectorEffect="non-scaling-stroke"
          strokeWidth={first ? 1.5 : 1}
          className={cn(
            first
              ? committed
                ? 'fill-brand-deep-raised stroke-brand-deep-foreground'
                : 'fill-brand-subtle stroke-foreground'
              : committed
                ? 'fill-none stroke-brand-deep-line'
                : 'fill-none stroke-line-strong',
          )}
        />,
      )
    }
  }

  return (
    <svg
      data-slot="kufic-grid-mark"
      viewBox="0 0 20 12"
      aria-hidden="true"
      focusable="false"
      className={cn('block h-15 w-25 shrink-0 rtl:-scale-x-100', className)}
      {...props}
    >
      {cells}
      <path
        // Centred in the first cell, 8.5px across at the mark's 100px width (never over 9px of red).
        d="M2 1.15l0.85 0.85l-0.85 0.85l-0.85-0.85z"
        vectorEffect="non-scaling-stroke"
        strokeWidth={1.5}
        paintOrder="stroke"
        className="fill-nuqta stroke-nuqta-frame"
      />
    </svg>
  )
}

export { KuficGridMark }
